import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Tabs,
  Tab,
  Chip,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { CardGiftcard, EmailOutlined, Tag, Block, Groups, Delete } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import userService from "../../services/userService";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import KpiCard from "../../components/common/KpiCard";
import ConfirmDialog from "../../components/common/ConfirmDialog";

const REFERRAL_STATUS_CHIP = {
  pending: { label: "Pending", color: "default" },
  qualified: { label: "Qualified", color: "info" },
  rewarded: { label: "Rewarded", color: "success" },
  void: { label: "Voided", color: "default" },
};

// Rewarded referrals show their actual snapshotted amount (fixed forever,
// even if the reward amount is later changed) — pending/qualified ones
// have no amount yet, so this shows the live config's "would-be" amount
// instead of a bare "—", scoped to whichever toggle (referrer/referred)
// that side's column is for.
const formatRewardCell = (referral, amountField, program, enabledField) => {
  if (referral.status === "rewarded") {
    return referral[amountField] > 0 ? `CI$${referral[amountField].toFixed(2)}` : "—";
  }
  if (referral.status === "pending" || referral.status === "qualified") {
    return program[enabledField] && program.rewardAmount > 0 ? `Expected CI$${program.rewardAmount.toFixed(2)}` : "—";
  }
  return "—";
};

// Admin view of the referral program: the reward config itself (amount,
// and whether the referrer/referred side actually gets paid — see
// referralService.maybeQualifyReferral, which reads this exact config),
// the full referral ledger (who referred whom, what it paid out, where it
// stands), plus the pre-launch Waiting List / Referral Codes visibility
// that existed before the program had any real mechanics.
const AdminReferrals = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState(0);
  const [waitlist, setWaitlist] = useState([]);
  const [codes, setCodes] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);

  const [settings, setSettings] = useState({ rewardAmount: 0, rewardReferrer: true, rewardReferred: true });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [rewardAmountInput, setRewardAmountInput] = useState("0");
  const [savingAmount, setSavingAmount] = useState(false);
  const [savingToggle, setSavingToggle] = useState(null);

  const [voidTarget, setVoidTarget] = useState(null);
  const [voiding, setVoiding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadAll = () => {
    setLoading(true);
    Promise.all([userService.getReferralWaitlist(), userService.getReferralCodes(), userService.getReferrals()])
      .then(([waitlistRes, codesRes, referralsRes]) => {
        setWaitlist(waitlistRes.data.data.entries || []);
        setCodes(codesRes.data.data.users || []);
        setReferrals(referralsRes.data.data.referrals || []);
      })
      .catch(() => enqueueSnackbar("Failed to load referral data", { variant: "error" }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
    userService
      .getReferralProgramSettings()
      .then(({ data }) => {
        const program = data.data?.referralProgram || {};
        setSettings(program);
        setRewardAmountInput(String(program.rewardAmount ?? 0));
      })
      .catch(() => enqueueSnackbar("Failed to load referral program settings", { variant: "error" }))
      .finally(() => setSettingsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveAmount = async () => {
    const amount = parseFloat(rewardAmountInput);
    if (Number.isNaN(amount) || amount < 0) {
      enqueueSnackbar("Enter a valid, non-negative amount", { variant: "error" });
      return;
    }
    setSavingAmount(true);
    try {
      const { data } = await userService.updateReferralProgramSettings({ rewardAmount: amount });
      setSettings(data.data.referralProgram);
      enqueueSnackbar("Reward amount updated", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to update reward amount", { variant: "error" });
    } finally {
      setSavingAmount(false);
    }
  };

  const handleToggle = async (field, checked) => {
    const previous = settings;
    setSettings((p) => ({ ...p, [field]: checked }));
    setSavingToggle(field);
    try {
      const { data } = await userService.updateReferralProgramSettings({ [field]: checked });
      setSettings(data.data.referralProgram);
    } catch (err) {
      setSettings(previous);
      enqueueSnackbar(err.response?.data?.message || "Failed to update", { variant: "error" });
    } finally {
      setSavingToggle(null);
    }
  };

  const handleVoid = async () => {
    if (!voidTarget) return;
    setVoiding(true);
    try {
      await userService.voidReferral(voidTarget._id);
      enqueueSnackbar("Referral voided", { variant: "success" });
      setVoidTarget(null);
      loadAll();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to void referral", { variant: "error" });
    } finally {
      setVoiding(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await userService.deleteReferral(deleteTarget._id);
      enqueueSnackbar("Referral deleted", { variant: "success" });
      setDeleteTarget(null);
      loadAll();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to delete referral", { variant: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const rewardedCount = referrals.filter((r) => r.status === "rewarded").length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Referral Program"
        subtitle="Reward settings, the full referral ledger, and who's claimed a code or asked to be notified"
      />

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
        <KpiCard
          label="Total Referrals"
          value={loading ? "—" : referrals.length.toString()}
          icon={<Groups />}
          iconBg="#EBF4FF"
          iconColor="#2B6CB0"
        />
        <KpiCard
          label="Rewarded"
          value={loading ? "—" : rewardedCount.toString()}
          icon={<CardGiftcard />}
          iconBg="#ECFDF5"
          iconColor="#0E9F6E"
        />
        <KpiCard
          label="On the Waitlist"
          value={loading ? "—" : waitlist.length.toString()}
          icon={<EmailOutlined />}
          iconBg="#FFFBEB"
          iconColor="#C05621"
        />
        <KpiCard
          label="Codes Claimed"
          value={loading ? "—" : codes.length.toString()}
          icon={<Tag />}
          iconBg="#F5F3FF"
          iconColor="#6D28D9"
        />
      </Box>

      <Card>
        <CardHeader title={<Typography fontWeight={700} fontSize={14}>Reward Settings</Typography>} sx={{ pb: 0 }} />
        {settingsLoading ? (
          <SkeletonLoader rows={2} />
        ) : (
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                size="small"
                label="Reward amount (CI$)"
                type="number"
                value={rewardAmountInput}
                onChange={(e) => setRewardAmountInput(e.target.value)}
                sx={{ width: 180 }}
                slotProps={{ htmlInput: { min: 0, step: "0.01" } }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleSaveAmount}
                disabled={savingAmount || parseFloat(rewardAmountInput) === settings.rewardAmount}
              >
                {savingAmount ? <CircularProgress size={16} /> : "Save"}
              </Button>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={!!settings.rewardReferrer}
                  disabled={savingToggle === "rewardReferrer"}
                  onChange={(e) => handleToggle("rewardReferrer", e.target.checked)}
                />
              }
              label="Reward the referrer"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!settings.rewardReferred}
                  disabled={savingToggle === "rewardReferred"}
                  onChange={(e) => handleToggle("rewardReferred", e.target.checked)}
                />
              }
              label="Reward the referred customer"
            />
          </CardContent>
        )}
      </Card>

      <Card>
        <Box sx={{ px: 2, borderBottom: "1px solid", borderColor: "divider" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ "& .MuiTab-root": { fontSize: 13, textTransform: "none", fontWeight: 600, minHeight: 44 } }}
          >
            <Tab label={`Referrals (${referrals.length})`} />
            <Tab label={`Waiting List (${waitlist.length})`} />
            <Tab label={`Referral Codes (${codes.length})`} />
          </Tabs>
        </Box>

        {loading ? (
          <SkeletonLoader rows={6} />
        ) : tab === 0 ? (
          referrals.length === 0 ? (
            <EmptyState icon="🤝" title="No referrals yet" description="Entries appear here the moment someone signs up using a customer's referral code." />
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Referrer</TableCell>
                    <TableCell>Referred</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Referrer Reward</TableCell>
                    <TableCell align="right">Referred Reward</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {referrals.map((r) => {
                    const statusInfo = REFERRAL_STATUS_CHIP[r.status] || { label: r.status, color: "default" };
                    return (
                      <TableRow key={r._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {r.referrer ? `${r.referrer.firstName} ${r.referrer.lastName}` : "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.referrer?.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {r.referredUser ? `${r.referredUser.firstName} ${r.referredUser.lastName}` : "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.referredUser?.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={statusInfo.label} color={statusInfo.color} variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          {formatRewardCell(r, "referrerRewardAmount", settings, "rewardReferrer")}
                        </TableCell>
                        <TableCell align="right">
                          {formatRewardCell(r, "referredRewardAmount", settings, "rewardReferred")}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12.5, color: "text.secondary" }}>
                          {dayjs(r.createdAt).format("MMM D, YYYY")}
                        </TableCell>
                        <TableCell align="right">
                          {(r.status === "pending" || r.status === "qualified") && (
                            <Tooltip title="Void this referral">
                              <IconButton size="small" onClick={() => setVoidTarget(r)}>
                                <Block fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {r.status === "void" && (
                            <Tooltip title="Delete this referral">
                              <IconButton size="small" onClick={() => setDeleteTarget(r)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )
        ) : tab === 1 ? (
          waitlist.length === 0 ? (
            <EmptyState icon="📬" title="No one on the waitlist yet" description="Entries appear here as customers click “Notify Me” on the referrals page." />
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>Joined</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {waitlist.map((entry) => (
                    <TableRow key={entry._id}>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12.5 }}>{entry.email}</TableCell>
                      <TableCell>
                        {entry.user ? (
                          <Typography variant="body2">
                            {entry.user.firstName} {entry.user.lastName}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12.5, color: "text.secondary" }}>
                        {dayjs(entry.createdAt).format("MMM D, YYYY · h:mm A")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )
        ) : codes.length === 0 ? (
          <EmptyState icon="🎁" title="No referral codes claimed yet" description="Entries appear here as customers claim their referral code on the referrals page." />
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Referral Code</TableCell>
                  <TableCell>On Waitlist Too?</TableCell>
                  <TableCell>Claimed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {codes.map((u) => (
                  <TableRow key={u._id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {u.firstName} {u.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: "text.secondary" }}>{u.email}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={<Tag sx={{ fontSize: 14 }} />}
                        label={u.referralCode}
                        sx={{ fontFamily: "monospace", fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell>
                      {u.referralWaitlistedAt ? (
                        <Chip size="small" label="Yes" color="success" variant="outlined" />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: "text.secondary" }}>
                      {u.referralCodeSetAt ? dayjs(u.referralCodeSetAt).format("MMM D, YYYY · h:mm A") : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>

      <ConfirmDialog
        open={!!voidTarget}
        title="Void this referral?"
        message="This stops it from ever qualifying or paying out a reward. It does not claw back any credit already issued."
        confirmLabel="Void Referral"
        confirmColor="error"
        loading={voiding}
        onConfirm={handleVoid}
        onCancel={() => setVoidTarget(null)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this referral?"
        message="This permanently removes it from the ledger. It's already voided, so nothing is owed — this is just cleanup."
        confirmLabel="Delete Referral"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
};

export default AdminReferrals;
